import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useOps } from "@/ops/OpsContext";
import { Loading } from "@/ops/ui";

const nextPath = () => {
  const n = sessionStorage.getItem("ops_next") || "";
  return n.startsWith("/operasyon") && !n.startsWith("/operasyon/giris") && !n.startsWith("/operasyon/sifre") ? n : "/operasyon";
};

function Shell({ title, children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--ink)] p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="mb-6 flex items-center gap-3">
          <img src="/assets/logo-shield.webp" alt="" className="h-11" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[color:var(--red)]">Rüzgâr Özel Güvenlik</p>
            <h1 className="text-xl font-extrabold text-zinc-950">{title}</h1>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

const MODES = [["ops", "Personel / Müşteri"], ["admin", "Yönetici"]];
const ID_PROPS = {
  admin: { label: "E-posta", inputMode: "email", placeholder: "ornek@firma.com" },
  ops: { label: "Telefon numarası veya e-posta", inputMode: "text", placeholder: "0532 123 45 67" },
};

function ModeTabs({ mode, setMode }) {
  return (
    <div className="mb-5 grid grid-cols-2 rounded-lg bg-zinc-100 p-1 text-sm font-semibold">
      {MODES.map(([k, l]) => (
        <button key={k} type="button" onClick={() => setMode(k)} className={`rounded-md py-2 transition-colors ${mode === k ? "bg-white text-zinc-950 shadow" : "text-zinc-500"}`} data-testid={`ops-login-tab-${k}`}>{l}</button>
      ))}
    </div>
  );
}

function LoginForm({ mode, onDone }) {
  const [f, setF] = useState({ identifier: "", password: "" });
  const [busy, setBusy] = useState(false);
  const idp = ID_PROPS[mode];
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await (mode === "admin" ? api.post("/auth/login", { email: f.identifier, password: f.password }) : api.post("/ops/auth/login", f));
      await onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <label className="grid gap-1 text-sm font-semibold text-zinc-700">
        {idp.label}
        <input value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} className="field-input" inputMode={idp.inputMode} autoComplete="username" placeholder={idp.placeholder} required data-testid="ops-login-identifier" />
      </label>
      <label className="grid gap-1 text-sm font-semibold text-zinc-700">
        Şifre
        <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="field-input" autoComplete="current-password" required data-testid="ops-login-password" />
      </label>
      <button disabled={busy} className="btn-red justify-center !py-3" data-testid="ops-login-submit">{busy ? "Giriş yapılıyor..." : "Giriş Yap"}</button>
      <p className="text-xs text-zinc-500">Hesaplar yalnızca yönetici tarafından açılır. Şifrenizi unuttuysanız yöneticinize başvurun.</p>
    </form>
  );
}

export function OpsLogin() {
  const { me, check, setLoggedOut } = useOps();
  const [next] = useState(nextPath);
  const [mode, setMode] = useState("ops");
  useEffect(() => {
    if (me && !me.must_change) sessionStorage.removeItem("ops_next");
  }, [me]);
  if (me === null) return <Loading />;
  if (me) return <Navigate to={me.must_change ? "/operasyon/sifre" : next} replace />;
  const done = async () => {
    await check();
    setLoggedOut(false);
  };
  return (
    <Shell title="Operasyon Girişi">
      <ModeTabs mode={mode} setMode={setMode} />
      <LoginForm key={mode} mode={mode} onDone={done} />
    </Shell>
  );
}

export function ChangePassword() {
  const { setMe, me } = useOps();
  const nav = useNavigate();
  const [f, setF] = useState({ current_password: "", new_password: "", again: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.again) return toast.error("Yeni şifreler eşleşmiyor.");
    try {
      await api.post("/ops/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      setMe({ ...me, must_change: false });
      toast.success("Şifreniz değiştirildi.");
      nav(nextPath(), { replace: true });
      sessionStorage.removeItem("ops_next");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const field = (k, label) => (
    <label className="grid gap-1 text-sm font-semibold text-zinc-700">{label}
      <input type="password" minLength={k === "current_password" ? 1 : 10} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field-input" required data-testid={`pw-${k}`} />
    </label>
  );
  return (
    <Shell title="Şifrenizi Belirleyin">
      <p className="mb-4 flex gap-2 rounded-md bg-zinc-50 p-3 text-sm text-zinc-600"><ShieldCheck className="h-5 w-5 shrink-0 text-[color:var(--red)]" /> Güvenliğiniz için yöneticinin verdiği geçici şifreyi değiştirmeniz gerekiyor. Yeni şifre en az 10 karakter olmalıdır.</p>
      <form onSubmit={submit} className="grid gap-4">
        {field("current_password", "Mevcut (geçici) şifre")}
        {field("new_password", "Yeni şifre")}
        {field("again", "Yeni şifre (tekrar)")}
        <button className="btn-red justify-center !py-3" data-testid="pw-submit">Şifreyi Kaydet</button>
      </form>
    </Shell>
  );
}
